import React, { useState, useEffect } from 'react'
import { simpananBerjangkaService } from '../../services/simpananBerjangkaService'
import { useAuthStore } from '../../stores/authStore.js'

const formatCurrency = (value) => {
  if (!value) return '0'
  return new Intl.NumberFormat('id-ID').format(value)
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
}

const TENOR_LABELS = {
  'SB_01': '1 Bulan',
  'SB_03': '3 Bulan',
  'SB_06': '6 Bulan',
  'SB_12': '12 Bulan',
  'SB_24': '24 Bulan'
}

const STATUS_COLORS = {
  AKTIF: 'badge-success',
  LUNAS: 'badge-primary',
  DITUTUP: 'badge-danger'
}

const TRANSACTION_COLORS = {
  SETORAN: 'success',
  ADMIN_FEE: 'warning',
  BUNGA: 'info',
  PENALTY: 'danger',
  PENUTUPAN: 'dark'
}

function SimpananBerjangkaDetail({ account, onClose, onRefresh }) {
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [showInterestModal, setShowInterestModal] = useState(false)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [interestPreview, setInterestPreview] = useState(null)
  const [jumlahBunga, setJumlahBunga] = useState('')

  // Calculate days to maturity
  const getDaysToMaturity = () => {
    if (!account.TanggalJatuhTempo) return '-'
    const now = new Date()
    const jatuhTempo = new Date(account.TanggalJatuhTempo)
    const diffTime = jatuhTempo - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const isEarlyClosure = () => {
    const now = new Date()
    const jatuhTempo = new Date(account.TanggalJatuhTempo)
    return now < jatuhTempo
  }

  const handleCalculateInterest = async () => {
    setLoading(true)
    try {
      const res = await simpananBerjangkaService.calculateInterest(account.Id)
      setInterestPreview(res.data?.data)
      setJumlahBunga(res.data?.data?.bungaDihitung?.toString() || '')
      setShowInterestModal(true)
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal menghitung bunga')
    } finally {
      setLoading(false)
    }
  }

  const handleWithdrawInterest = async () => {
    if (!jumlahBunga || parseFloat(jumlahBunga) <= 0) {
      alert('Masukkan jumlah bunga yang akan ditarik')
      return
    }

    setActionLoading(true)
    try {
      await simpananBerjangkaService.withdrawInterest(account.Id, parseFloat(jumlahBunga))
      alert('Bunga berhasil ditarik!')
      setShowInterestModal(false)
      onRefresh()
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal menarik bunga')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCloseAccount = async (jenisPenutupan) => {
    if (!confirm(`Yakin akan menutup rekening ${account.NoRekening}?`)) return

    setActionLoading(true)
    try {
      await simpananBerjangkaService.closeAccount(account.Id, jenisPenutupan)
      alert('Rekening berhasil ditutup!')
      setShowCloseModal(false)
      onRefresh()
      onClose()
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal menutup rekening')
    } finally {
      setActionLoading(false)
    }
  }

  const getTotalPencairan = () => {
    const totalBunga = account.TotalBunga || 0
    return (account.JumlahSetoran || 0) + totalBunga
  }

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">Detail Simpanan Berjangka</h5>
            <button type="button" className="close text-white" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>

          <div className="modal-body">
            {/* Account Info */}
            <div className="row">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Informasi Rekening</h6>
                  </div>
                  <div className="card-body p-2">
                    <table className="table table-sm table-borderless mb-0">
                      <tr>
                        <td width="40%">No. Rekening</td>
                        <td><strong>{account.NoRekening}</strong></td>
                      </tr>
                      <tr>
                        <td>NBA</td>
                        <td>{account.NBA}</td>
                      </tr>
                      <tr>
                        <td>Nama Anggota</td>
                        <td>{account.Nama || account.NamaAnggota}</td>
                      </tr>
                      <tr>
                        <td>Tenor</td>
                        <td>{TENOR_LABELS[account.KodeJenis] || account.KodeJenis}</td>
                      </tr>
                      <tr>
                        <td>Status</td>
                        <td>
                          <span className={`badge ${STATUS_COLORS[account.Status] || 'badge-secondary'}`}>
                            {account.Status}
                          </span>
                          {account.Status === 'AKTIF' && (
                            <span className="ml-2 text-muted">
                              ({getDaysToMaturity()} hari lagi)
                            </span>
                          )}
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Informasi Keuangan</h6>
                  </div>
                  <div className="card-body p-2">
                    <table className="table table-sm table-borderless mb-0">
                      <tr>
                        <td width="40%">Setoran (Gross)</td>
                        <td className="text-right">
                          <strong>Rp {formatCurrency(account.JumlahSetoran)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>Biaya Admin</td>
                        <td className="text-right text-danger">
                          Rp {formatCurrency(account.BiayaAdmin)}
                        </td>
                      </tr>
                      <tr>
                        <td>Bunga per Tahun</td>
                        <td className="text-right">{account.BungaPersen}%</td>
                      </tr>
                      <tr>
                        <td>Total Bunga</td>
                        <td className="text-right text-success">
                          Rp {formatCurrency(account.TotalBunga || 0)}
                        </td>
                      </tr>
                      <tr className="table-info">
                        <td><strong>Total Pencairan</strong></td>
                        <td className="text-right">
                          <strong className="text-success">Rp {formatCurrency(getTotalPencairan())}</strong>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="row mt-3">
              <div className="col-md-4">
                <div className="alert alert-secondary mb-0 py-2">
                  <small>Tanggal Setoran</small>
                  <div className="font-weight-bold">{formatDate(account.TanggalSetoran)}</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="alert alert-info mb-0 py-2">
                  <small>Jatuh Tempo</small>
                  <div className="font-weight-bold">{formatDate(account.TanggalJatuhTempo)}</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="alert alert-warning mb-0 py-2">
                  <small>Metode Bunga</small>
                  <div className="font-weight-bold">
                    {account.MetodeBunga === 'CASH' ? 'Tunai' : 'Mutasi ke Sukarela'}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions for AKTIF accounts */}
            {account.Status === 'AKTIF' && (
              <div className="mt-3">
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Aksi</h6>
                  </div>
                  <div className="card-body p-2">
                    <div className="row">
                      <div className="col-md-4">
                        <button
                          className="btn btn-primary btn-block"
                          onClick={handleCalculateInterest}
                          disabled={loading}
                        >
                          <i className="fas fa-calculator mr-1"></i>
                          Hitung Bunga
                        </button>
                      </div>
                      <div className="col-md-4">
                        <button
                          className="btn btn-success btn-block"
                          onClick={() => setShowCloseModal(true)}
                          disabled={actionLoading}
                        >
                          <i className="fas fa-check-circle mr-1"></i>
                          Tutup Rekening
                        </button>
                      </div>
                      {isEarlyClosure() && (
                        <div className="col-md-4">
                          <div className="alert alert-danger mb-0 py-2">
                            <small>
                              <i className="fas fa-exclamation-triangle mr-1"></i>
                              Penutupan dini akan dikenakan penalty
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Transaction History */}
            <div className="mt-3">
              <h6>Riwayat Transaksi</h6>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead className="thead-light">
                    <tr>
                      <th>Tanggal</th>
                      <th>No. Transaksi</th>
                      <th>Jenis</th>
                      <th>Jumlah</th>
                      <th>Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(account.transaksi || []).map((trx) => (
                      <tr key={trx.Id}>
                        <td>{formatDate(trx.TglTransaksi)}</td>
                        <td>{trx.NoTransaksi}</td>
                        <td>
                          <span className={`badge badge-${TRANSACTION_COLORS[trx.JenisTransaksi] || 'secondary'}`}>
                            {trx.JenisTransaksi}
                          </span>
                        </td>
                        <td className="text-right">
                          {trx.JenisTransaksi === 'SETORAN' ? '+' : '-'} Rp {formatCurrency(trx.Jumlah)}
                        </td>
                        <td>{trx.Keterangan}</td>
                      </tr>
                    ))}
                    {(!account.transaksi || account.transaksi.length === 0) && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          Tidak ada transaksi
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Tutup
            </button>
          </div>
        </div>
      </div>

      {/* Interest Calculation Modal */}
      {showInterestModal && interestPreview && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Kalkulasi Bunga</h5>
                <button type="button" className="close" onClick={() => setShowInterestModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <table className="table table-sm table-borderless">
                  <tr>
                    <td>Setoran</td>
                    <td className="text-right">Rp {formatCurrency(interestPreview.jumlahSetoran)}</td>
                  </tr>
                  <tr>
                    <td>Bunga per Tahun</td>
                    <td className="text-right">{interestPreview.bungaPerTahun}%</td>
                  </tr>
                  <tr>
                    <td>Bulan Berlalu</td>
                    <td className="text-right">{interestPreview.bulanBerlalu} bulan</td>
                  </tr>
                  <tr className="table-success">
                    <td><strong>Bunga Dihitung</strong></td>
                    <td className="text-right">
                      <strong>Rp {formatCurrency(interestPreview.bungaDihitung)}</strong>
                    </td>
                  </tr>
                </table>

                <div className="form-group mt-3">
                  <label>Jumlah Bunga yang Ditarik</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">Rp</span>
                    </div>
                    <input
                      type="number"
                      className="form-control"
                      value={jumlahBunga}
                      onChange={(e) => setJumlahBunga(e.target.value)}
                      max={interestPreview.bungaDihitung}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowInterestModal(false)}
                >
                  Batal
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleWithdrawInterest}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Memproses...' : 'Tarik Bunga'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Account Confirmation Modal */}
      {showCloseModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Konfirmasi Penutupan</h5>
                <button type="button" className="close" onClick={() => setShowCloseModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  {isEarlyClosure() ? (
                    <span>Peringatan! Penutupan sebelum jatuh tempo akan dikenakan penalty.</span>
                  ) : (
                    <span>Rekening akan ditutup dan dana akan dicairkan.</span>
                  )}
                </div>

                <table className="table table-sm table-borderless">
                  <tr>
                    <td>Pokok</td>
                    <td className="text-right">Rp {formatCurrency(account.JumlahSetoran)}</td>
                  </tr>
                  <tr>
                    <td>Bunga</td>
                    <td className="text-right text-success">+ Rp {formatCurrency(account.TotalBunga || 0)}</td>
                  </tr>
                  {isEarlyClosure() && (
                    <tr className="text-danger">
                      <td>Penalty</td>
                      <td className="text-right">- Rp 0 ( belum dihitung)</td>
                    </tr>
                  )}
                  <tr className="table-info">
                    <td><strong>Total Pencairan</strong></td>
                    <td className="text-right">
                      <strong>Rp {formatCurrency(getTotalPencairan())}</strong>
                    </td>
                  </tr>
                </table>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCloseModal(false)}
                >
                  Batal
                </button>
                <button
                  className={`btn ${isEarlyClosure() ? 'btn-danger' : 'btn-success'}`}
                  onClick={() => handleCloseAccount(isEarlyClosure() ? 'EARLY' : 'JATUH_TEMPO')}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Memproses...' : 'Konfirmasi'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SimpananBerjangkaDetail